import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Typography, CircularProgress, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import axiosInstance from "../../../Config/axiosInstance";
import EditCostModal from "../CostControl/EditCostModal";
import "./CostViewPage.css";
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFileArchive, FaFile } from "react-icons/fa";

const CostViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

    //Functie de formatare suma
  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';

    const n = Number(value);
    if (Number.isNaN(n)) return '';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  // Verificare permisiuni edit
  useEffect(() => {
    const requiredPath = "/cost-control-administrative";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const checkPermission = (key) =>
      permissions.some(page => {
        try {
          const pagePath = new URL(page.url).pathname;
          return pagePath === requiredPath && page[key] === true;
        } catch {
          return false;
        }
      });

    setCanEdit(checkPermission('can_edit'));
  }, []);


  //Fech date
  const fetchData = useCallback(async () => {
    try {
      if (location.state?.costItem) {
        setCostData(location.state.costItem);
        setLoading(false);
        return;
      }

      const [
        costControlResponse,
        projectCodesResponse,
        budgetTypeResponse,
        expensesTypeResponse,
        departmentResponse,
        employeeResponse,
        supplierResponse,
        userResponse,
      ] = await Promise.all([
        axiosInstance.get("/cost-administrative/"),
        axiosInstance.get("/project-codes/"),
        axiosInstance.get("/budget-type/"),
        axiosInstance.get("/expenses-type/"),
        axiosInstance.get("/departments/"),
        axiosInstance.get("/employees/"),
        axiosInstance.get("/suppliers/"),
        axiosInstance.get("/users/"),
      ]);

      const projectCodeMap = projectCodesResponse.data.reduce((acc, code) => {
        acc[code.id] = code.code;
        return acc;
      }, {});

      const budgetTypeMap = budgetTypeResponse.data.reduce((acc, budget) => {
        acc[budget.id] = budget.type;
        return acc;
      }, {});

      const expensesTypeMap = expensesTypeResponse.data.reduce((acc, exp) => {
        acc[exp.id] = exp.type;
        return acc;
      }, {});

      const departmentMap = departmentResponse.data.reduce((acc, dept) => {
        acc[dept.id] = dept.name;
        return acc;
      }, {});

      const employeeMap = employeeResponse.data.reduce((acc, emp) => {
        acc[emp.id] = `${emp.first_name} ${emp.last_name}`;
        return acc;
      }, {});

      const supplierMap = supplierResponse.data.reduce((acc, sup) => {
        acc[sup.id] = sup.name;
        return acc;
      }, {});

      const usersMap = userResponse.data.reduce((acc, user) => {
        acc[user.id] = `${user.first_name} ${user.last_name}`;
        return acc;
      }, {});

      const costRecord = costControlResponse.data.find(
        (item) => item.id === parseInt(id)
      );

      if (costRecord) {
        let resourceName = "-";
        if (costRecord.resource) {
          resourceName = employeeMap[costRecord.resource] || "-";
        } else if (costRecord.supplier) {
          resourceName = supplierMap[costRecord.supplier] || "-";
        } else if (costRecord.department_resource) {
          resourceName = departmentMap[costRecord.department_resource] || "-";
        }

        const mappedCostData = {
          id: costRecord.id,
          budget_type: budgetTypeMap[costRecord.budget_type] || "-",
          project_code: projectCodeMap[costRecord.project_code] || "-",
          resource: resourceName,
          department: departmentMap[costRecord.department] || "-",
          expenses_type: expensesTypeMap[costRecord.expenses_type] || "-",
          pay_date: costRecord.pay_date
            ? new Date(costRecord.pay_date).toLocaleDateString("ro-RO")
            : "-",
          remarks: costRecord.remarks,
          vat_payer: costRecord.vat_payer ? "Yes" : "No",
          price_without_vat: costRecord.without_vat || "0.00",
          vat: costRecord.vat || "0",
          price_with_vat: costRecord.with_vat || "0.00",
          month: costRecord.month || "-",
          year: costRecord.year || "-",
          total_cost: costRecord.with_vat || "0.00",
          attachment: costRecord.document || null,
          registration_date: costRecord.registration_date
            ? new Date(costRecord.registration_date).toLocaleDateString("ro-RO")
            : "-",
          user: usersMap[costRecord.user] || "-",
          status: costRecord.status,
          cost_type: costRecord.tip_cost,
          original: costRecord,
        };

        setCostData(mappedCostData);
      } else {
        setCostData(null);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching cost data:", error.response?.data || error);
      setLoading(false);
    }
  }, [id, location.state]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  //Stare de loading
  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading...</Typography>
      </div>
    );
  }

  //In caz ca nu se gaseste costul
  if (!costData) {
    return (
      <div className="loading-container">
        <Typography>Cost record not found.</Typography>
      </div>
    );
  }

  // Deschide modalul de editare
  const handleEditClick = () => {
    setOpenEditModal(true);
  };

  const handleUpdate = async () => {
    setOpenEditModal(false);
    await fetchData();
  };

  return (
    <div className="cost-view-page">
      {/* === HEADER BACK + EDIT === */}
      <div className="cost-back-container">
        <div className="back-left">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="cost-back">Back</h2>
        </div>
        {canEdit && (
          <div className="top-right-actions">
            <Button
              onClick={handleEditClick}
              className="edit-btn"
              title="Edit"
            >
              <EditNoteSharpIcon fontSize="small" />
              Edit
            </Button>
          </div>
        )}
      </div>

      <hr className="title-separator" />

      {/* === CARDURI === */}
      <div className="cost-container">
        {/* LEFT CARD */}
        <div className="cost-card left-card">
          <h3 className="section-title">
            Project code - {costData.project_code || "N/A"}
          </h3>

          <div className="cost-grid">
            <DetailItem label="Budget type" value={costData.budget_type} />
            <DetailItem label="Resource" value={costData.resource} />
            <DetailItem label="Department" value={costData.department} />
            <DetailItem label="Expense type" value={costData.expenses_type} />
            <DetailItem label="Remarks" value={costData.remarks} />
            <DetailItem
              label="Registration date"
              value={costData.registration_date}
            />
          </div>

          <div className="created-by-footer">
            <span className="created-by">
              Created by <strong>{costData.user || "—"}</strong>
            </span>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="right-column">
          {/* === CARD INVOICE === */}
          <div className="cost-card right-card">
            <div className="invoice-top">
              <div className="invoice-header">
                <h3>INVOICE</h3>
                <span
                  className={
                    costData.status === "Paid"
                      ? "status-badge-paid"
                      : "status-badge-unpaid"
                  }
                >
                  {costData.status === "Paid" ? "🟢 Paid" : "🟡 Unpaid"}
                </span>
              </div>

              <div className="cost-status">
                <div className="cost-type-chip">
                  <span className="cost-title">COST</span>
                  <span
                    className={
                      costData.cost_type === "Direct"
                        ? "status-badge-paid"
                        : "status-badge-unpaid"
                    }
                  >
                    {costData.cost_type === "Direct" ? "🟢 Direct" : "🟡 Indirect"}
                  </span>
                </div>
              </div>
            </div>

            <div className="invoice-body">
              <div className="price-row">
                <span className="price-label">VAT Payer :</span>
                <span className="price-value">{costData.vat_payer}</span>
              </div>

              <div className="price-row">
                <span className="price-label">Price without VAT :</span>
                <span className="price-value">
                  {formatNumber(costData.price_without_vat)} lei
                </span>
              </div>

              <div className="price-row">
                <span className="price-label">VAT :</span>
                <span className="price-value vat-chip">{costData.vat}%</span>
              </div>

              <div className="price-row">
                <span className="price-label">Price with VAT :</span>
                <span className="total-value">
                  {formatNumber(costData.price_with_vat)} lei
                </span>
              </div>

              <div className="price-row">
                <span className="price-label">Pay date :</span>
                <span className="price-value">{costData.pay_date || "-"}</span>
              </div>

              <div className="price-row">
                <span className="price-label">Month :</span>
                <span className="price-value">{costData.month || "-"}</span>
              </div>

              <div className="price-row">
                <span className="price-label">Year :</span>
                <span className="price-value">{costData.year || "-"}</span>
              </div>
            </div>
          </div>

          {/* === CARD EXTRA === */}
          <div className="cost-card extra-card">
            <h3 className="extra-title">ATTACHMENT</h3>
            <div className="extra-item">
              {costData.attachment ? (
                (() => {
                  let backendOrigin = "";
                  const baseURL = axiosInstance?.defaults?.baseURL;

                  if (baseURL) {
                    try {
                      backendOrigin = new URL(baseURL).origin;
                    } catch {
                      console.warn(" axiosInstance baseURL invalid:", baseURL);
                    }
                  }

                  if (!backendOrigin) {
                    backendOrigin = window.location.origin;
                  }

                  const attachmentPath = costData.attachment;
                  const fileUrl = attachmentPath.startsWith("/")
                    ? `${backendOrigin}${attachmentPath}`
                    : `${backendOrigin}/${attachmentPath}`;
                  ;

                  const fileName = extractFileName(costData.attachment);
                  const ext = fileName.split(".").pop().toLowerCase();
                  const isPreviewable = ["pdf", "jpg", "jpeg", "png"].includes(ext);

                  let IconComponent = FaFile;
                  let iconColor = "#555";

                  switch (ext) {
                    case "pdf":
                      IconComponent = FaFilePdf;
                      iconColor = "#e74c3c";
                      break;
                    case "doc":
                    case "docx":
                      IconComponent = FaFileWord;
                      iconColor = "#2b579a";
                      break;
                    case "xls":
                    case "xlsx":
                      IconComponent = FaFileExcel;
                      iconColor = "#217346";
                      break;
                    case "jpg":
                    case "jpeg":
                    case "png":
                      IconComponent = FaFileImage;
                      iconColor = "#0f1af1";
                      break;
                    case "zip":
                    case "rar":
                      IconComponent = FaFileArchive;
                      iconColor = "#e67e22";
                      break;
                    default:
                      IconComponent = FaFile;
                      iconColor = "#555";
                      break;
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        maxWidth: "250px",
                      }}
                    >
                      <IconComponent style={{ color: iconColor, fontSize: "18px" }} />
                      <a
                        href={fileUrl}
                        target={isPreviewable ? "_blank" : "_self"}
                        rel="noopener noreferrer"
                        download={isPreviewable ? undefined : fileName}
                        className="attachment-link"
                        style={{
                          color: "#1976d2",
                          textDecoration: "none",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={isPreviewable ? "Open file" : "Download file"}
                      >
                        {fileName}
                      </a>
                    </div>
                  );
                })()
              ) : (
                <span className="no-attachment">--</span>
              )}
            </div>
          </div>



        </div>
      </div>

      {/* === MODAL DE EDITARE === */}
      {openEditModal && (
        <EditCostModal
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          onUpdate={handleUpdate}
          costData={costData.original || costData}
        />

      )}
    </div>
  );
};

const extractFileName = (url) => {
  if (!url) return "View file";
  const parts = url.split("/");
  return parts[parts.length - 1];
};

const DetailItem = ({ label, value }) => (
  <div className="detail-item">
    <span className="cost-label">{label} - </span>
    <span className="cost-value">{value}</span>
  </div>
);

export default CostViewPage;
